<template>
  <Dialog :open="open" @update:open="handleClose" :title="title" size="lg">
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug</label>
        <div v-if="mode === 'create'" class="space-y-1">
          <!-- Org prefix (locked) + optional sub-path + name -->
          <div class="flex items-center gap-1 text-sm">
            <span class="rounded bg-gray-100 dark:bg-gray-700 px-2 py-1.5 text-gray-500 dark:text-gray-400 font-mono text-xs shrink-0">
              {{ orgSlug || 'org' }}/
            </span>
            <Input
              v-model="form.slugSub"
              placeholder="sub-path (optional)"
              class="flex-1 font-mono text-xs"
            />
            <span class="text-gray-400 shrink-0">/</span>
            <Input
              v-model="form.slugName"
              placeholder="name"
              :error="!!errors.slug"
              class="flex-1 font-mono text-xs"
            />
          </div>
          <p class="text-[10px] text-gray-400 font-mono">→ {{ fullSlug || (orgSlug + '/name') }}</p>
          <p v-if="errors.slug" class="text-xs text-red-600 dark:text-red-400">{{ errors.slug }}</p>
        </div>
        <div v-else class="rounded bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm font-mono text-gray-600 dark:text-gray-300">
          {{ server?.slug }}
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Display Name
        </label>
        <Input v-model="form.display_name" :error="!!errors.display_name" class="w-full" />
        <p v-if="errors.display_name" class="mt-1 text-xs text-red-600 dark:text-red-400">
          {{ errors.display_name }}
        </p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description
        </label>
        <Textarea v-model="form.description" :rows="2" class="w-full" />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Capabilities
        </label>
        <div class="grid grid-cols-2 gap-2">
          <label
            v-for="cap in capabilityKeys"
            :key="cap"
            class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <input
              type="checkbox"
              :checked="form.capabilities[cap]"
              class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              @change="form.capabilities[cap] = ($event.target as HTMLInputElement).checked"
            />
            {{ cap }}
          </label>
        </div>
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" @click="handleClose">Cancel</Button>
        <Button type="submit" variant="primary">
          {{ mode === 'edit' ? 'Update' : 'Create' }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { Button, Dialog, Input, Textarea } from '@asabdev/asab-ui';
  import type {
    VirtualServer,
    VirtualServerCreate,
    VirtualServerUpdate,
    VServerCapabilities,
  } from '@/lib/mcp/types';

  interface Props {
    open: boolean;
    mode: 'create' | 'edit';
    server?: VirtualServer | null;
    orgSlug?: string;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (
      e: 'submit',
      payload:
        | { mode: 'create'; data: VirtualServerCreate }
        | { mode: 'edit'; id: string; data: VirtualServerUpdate },
    ): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  const capabilityKeys: (keyof VServerCapabilities)[] = [
    'tools',
    'resources',
    'prompts',
    'sampling',
    'elicitation',
    'tasks',
  ];

  const defaultCaps = (): VServerCapabilities => ({
    tools: true,
    resources: false,
    prompts: false,
    sampling: false,
    elicitation: false,
    tasks: false,
  });

  const form = ref({
    slugName: '',   // just the name part (user-editable)
    slugSub: '',    // optional sub-path (user-editable)
    display_name: '',
    description: '',
    capabilities: defaultCaps(),
  });

  // Full slug = {orgSlug}/{slugSub}/{slugName} or {orgSlug}/{slugName}
  const fullSlug = computed(() => {
    const parts = [props.orgSlug, form.value.slugSub.trim(), form.value.slugName.trim()]
      .filter(Boolean)
      .join('/');
    return parts;
  });

  const errors = ref<Record<string, string>>({});
  const title = computed(() => (props.mode === 'edit' ? 'Edit Virtual Server' : 'Create Virtual Server'));

  watch(
    () => props.open,
    (open) => {
      if (open) {
        // Parse existing slug: strip org prefix, split remaining into sub + name
        let existingSlug = props.server?.slug ?? '';
        if (props.orgSlug && existingSlug.startsWith(props.orgSlug + '/')) {
          existingSlug = existingSlug.slice(props.orgSlug.length + 1);
        }
        const parts = existingSlug.split('/');
        const slugName = parts.pop() ?? '';
        const slugSub = parts.join('/');

        form.value = {
          slugName,
          slugSub,
          display_name: props.server?.display_name ?? '',
          description: props.server?.description ?? '',
          capabilities: props.server?.capabilities
            ? { ...props.server.capabilities }
            : defaultCaps(),
        };
        errors.value = {};
      }
    },
  );

  const handleClose = () => emit('update:open', false);

  const validate = (): boolean => {
    errors.value = {};
    if (!form.value.slugName.trim()) errors.value.slug = 'Name is required';
    if (!form.value.display_name.trim()) errors.value.display_name = 'Display name is required';
    return Object.keys(errors.value).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    if (props.mode === 'edit' && props.server) {
      emit('submit', {
        mode: 'edit',
        id: props.server.id,
        data: {
          display_name: form.value.display_name.trim(),
          description: form.value.description.trim() || undefined,
          capabilities: form.value.capabilities,
        },
      });
    } else {
      emit('submit', {
        mode: 'create',
        data: {
          slug: fullSlug.value,
          display_name: form.value.display_name.trim(),
          description: form.value.description.trim() || undefined,
          capabilities: form.value.capabilities,
        },
      });
    }
    handleClose();
  };
</script>
